import { graphql, type HeadProps, type PageProps } from 'gatsby';
import Layout from '../components/Layout';
import MetaTags from '@/components/MetaTags/MetaTags';
import { isPageTranslated } from '@/utils/localization';

const ArticlePageTemplate = ({
  data,
  pageContext,
}: PageProps<Queries.ArticlePageTemplateQuery>) => {
  const availableLanguages = data.allKontentItemArticlePage.group.flatMap((item) => item.distinct);
  return (
    <Layout
      pageContext={pageContext}
      elements={data.kontentItemArticlePage!.elements}
      availableLanguages={availableLanguages}
    />
  );
};

export const Head = ({
  data,
  pageContext,
}: HeadProps<Queries.ArticlePageTemplateQuery, PageContext>) => {
  const isTranslated = isPageTranslated({
    preferredLanguage: data.kontentItemArticlePage?.preferred_language,
    pageLanguage: data.kontentItemArticlePage?.system.language,
  });

  return (
    <MetaTags
      language={pageContext.language}
      metadata={data.kontentItemArticlePage!.elements}
      shouldNotIndex={!isTranslated}
      isTranslated={isTranslated}
    />
  );
};

export const ArticlePageTemplateQuery = graphql`
  query ArticlePageTemplate($pageCodename: String!, $language: String!) {
    kontentItemArticlePage(
      preferred_language: { eq: $language }
      system: { codename: { eq: $pageCodename } }
    ) {
      elements {
        ...TWITTER_META
        ...OPENGRAPH_META
        ...SEO_META
        ...PAGE_LAYOUT
        building_blocks {
          value {
            ... on kontent_item {
              system {
                type
                id
              }
            }
            ...ACCORDION
            ...ANNOUNCEMENT_BUTTON
            ...ARTICLE_POST
            ...CARDS_BLOCK
            ...CAROUSEL_SECTION
            ...COLUMN_BLOCK
            ...COMBO_BLOCK
            ...COMPARISON_TABLE
            ...CTA_BLOCK
            ...FOOTER
            ...FORM_CONTAINER
            ...HEADER
            ...HERO_BLOCK_ARTICLE_DETAIL_1
            ...HERO_BLOCK_ARTICLE_DETAIL_2
            ...HERO_BLOCK_HOMEPAGE
            ...HERO_BLOCK_INDEX
            ...HERO_EMAIL_FORM
            ...HTML
            ...IFRAME
            ...IMAGE_COLUMN_BLOCK
            ...INDUSTRY_LOGO_BLOCK
            ...INTERSTITIAL
            ...JUMP_TO_MENU
            ...LANGUAGE_GRID_BLOCK
            ...LANGUAGE_GRID_BLOCK_V2
            ...LOGO_BLOCK
            ...MEDIA_CAROUSEL
            ...MID_PAGE_CTA
            ...MID_PAGE_FORM_CONTAINER
            ...MID_PAGE_SEARCH
            ...MODAL
            ...N5050_BLOCK
            ...N5050_CARD_BLOCK
            ...OPEN_SOURCE_SOLUTIONS
            ...QUOTE_BLOCK
            ...SECONDARY_HERO_BLOCK
            ...SECONDARY_NAVIGATION
            ...SECTION_INTRODUCTION
            ...SINGLE_MEDIA
            ...STATBAR_BLOCK
            ...STICKY_SIDEBAR
            ...SUPER_CTA_BLOCK
            ...TABBED_BLOCK
            ...TERTIARY_NAVIGATION
            ...TESTIMONIAL_BLOCK
            ...TIMELINE_BLOCK
            ...VULNERABILITY_DISCLOSURE_TABLE
          }
        }
        creation_date {
          value
        }
      }
      preferred_language
      system {
        language
      }
    }

    allKontentItemArticlePage(filter: { system: { codename: { eq: $pageCodename } } }) {
      group(field: { system: { language: SELECT } }) {
        distinct(field: { system: { language: SELECT } })
      }
    }
  }
`;

export default ArticlePageTemplate;
